import { Component } from "@theme/component";
import { CartAddEvent, SubscriptionOpenEvent } from "@theme/events";
import { initOptionScroll } from "@theme/card-option-scroll";
import { buildFeeItem } from "@theme/bcrs-fee";
import { withDistributorIdProperty } from "@theme/utilities";

/**
 * Custom product card web component with Add-to-cart and Subscribe actions.
 *
 * @typedef {Object} CustomProductCardRefs
 * @property {HTMLButtonElement} addBtn - The add to cart button
 * @property {HTMLElement} addBtnContent - The add button label
 * @property {HTMLElement} addBtnSpinner - The add button loading icon
 * @property {HTMLButtonElement} [subscribeBtn] - The subscribe button (optional)
 * @property {HTMLElement} [subscribeBtnContent] - The subscribe button label
 * @property {HTMLElement} [subscribeBtnSpinner] - The subscribe button loading icon
 *
 * @extends {Component<CustomProductCardRefs>}
 */
class CustomProductCard extends Component {
  connectedCallback() {
    super.connectedCallback();

    this._onAdd = () => this.handleAdd();
    this._onSubscribe = () => this.handleSubscribe();

    this.refs.addBtn?.addEventListener("click", this._onAdd);
    this.refs.subscribeBtn?.addEventListener("click", this._onSubscribe);

    this.#initMediaCarousel();
    this.#initVariantPicker();

    // Scroll arrows for overflowing option pill rows (mirrors the NG card).
    try {
      initOptionScroll(this);
    } catch (error) {
      console.error("custom-product-card: option scroll init failed", error);
    }
  }

  disconnectedCallback() {
    this.refs.addBtn?.removeEventListener("click", this._onAdd);
    this.refs.subscribeBtn?.removeEventListener("click", this._onSubscribe);
    this.#teardownMediaCarousel();
    this.#teardownVariantPicker();

    super.disconnectedCallback();
  }

  /**
   * Wires up the variant option picker. Selecting option values resolves the
   * matching variant from the embedded map and updates price, rewards, image,
   * add-to-cart variant and the PDP links. Until every option is chosen the card
   * shows the "Details" CTA (linking to the PDP); once a full variant is resolved
   * the Add/Subscribe actions are revealed.
   */
  #initVariantPicker() {
    const picker = this.querySelector("[data-cpc-variant-picker]");
    if (!picker) return;

    this._picker = picker;
    this._optionCount = Number(picker.dataset.optionCount || 0);
    // Preview-only (jewelry/lipstick) — swatch selection swaps the tile
    // image but never resolves a purchasable variant or flips the CTA.
    this._previewOnly = picker.dataset.cpcPreviewOnly === "true";
    this._variantMap = this.#parseVariantMap();
    this._selectedOptions = {};

    this._onOptionChange = (event) => {
      const input = event.target.closest("[data-cpc-input]");
      if (!input) return;
      const index = Number(input.dataset.optionIndex);
      this._selectedOptions[index] = input.dataset.value;

      const fieldset = input.closest("[data-cpc-option]");
      const valueLabel = fieldset?.querySelector("[data-cpc-option-value]");
      if (valueLabel) valueLabel.textContent = input.dataset.display || "";

      if (this._previewOnly) {
        this.#previewOption(index, input.dataset.value);
      } else {
        this.#resolveVariant();
      }
      this.#syncHoverImage();
    };
    picker.addEventListener("change", this._onOptionChange);
  }

  /**
   * Preview-only swatch handling: swap the tile image to the first variant that
   * carries the selected value. Does NOT set a purchasable variant id, dispatch
   * a variant update, or reveal the Add CTA — the card stays on "Details".
   * @param {number} index - The selected option's index.
   * @param {string} value - The selected option value.
   */
  #previewOption(index, value) {
    const match = this._variantMap.find((variant) => variant.options?.[Number(index)] === value);
    if (match) this.#updateImage(match);
  }

  #teardownVariantPicker() {
    this._picker?.removeEventListener("change", this._onOptionChange);
  }

  #parseVariantMap() {
    const script = this.querySelector("[data-cpc-variant-map]");
    if (!script?.textContent) return [];
    try {
      return JSON.parse(script.textContent);
    } catch {
      return [];
    }
  }

  /**
   * Resolves the variant matching the current option selection and, when a full
   * match is found, updates the card. Until then it keeps the Details CTA.
   */
  #resolveVariant() {
    const allSelected = Object.keys(this._selectedOptions).length >= this._optionCount && this._optionCount > 0;
    const match = allSelected
      ? this._variantMap.find((variant) =>
          Object.entries(this._selectedOptions).every(([index, value]) => variant.options?.[Number(index)] === value)
        )
      : null;

    // Show Details until every option is chosen and resolves to a real variant;
    // then reveal Add/Subscribe for the resolved variant.
    if (!allSelected || !match) {
      this.#showDetailsCta();
      return;
    }

    this.#applyVariant(match);
    // An unavailable, backordered, or pre-order variant shows Details (→ PDP)
    // rather than a disabled "Sold out" Add — those flows complete on the PDP.
    if (match.available !== false && !match.backorder && !match.preorder) {
      this.#showAddCta();
    } else {
      this.#showDetailsCta();
    }
  }

  #applyVariant(match) {
    this.dataset.variantId = String(match.id);
    this.#updatePrice(match);
    this.#updateImage(match);
    this.#updateProductLinks(match.url);
    this.#dispatchVariantUpdate(match.id);
  }

  #showDetailsCta() {
    this.refs.detailsBtn?.classList.remove("tw:hidden");
    this.querySelector("[data-cpc-actions]")?.classList.add("tw:hidden");
  }

  // Only ever called for a buyable variant — unavailable ones take
  // #showDetailsCta instead (MA-2309), so there is no disabled/"Sold out" state
  // and no sold-out Subscribe to suppress (the MA-1707 case that hid Subscribe
  // beside a disabled Add can no longer arise).
  #showAddCta() {
    this.refs.detailsBtn?.classList.add("tw:hidden");
    const actions = this.querySelector("[data-cpc-actions]");
    actions?.classList.remove("tw:hidden");
    if (this.refs.addBtn) {
      this.refs.addBtn.disabled = false;
      const content = this.refs.addBtnContent;
      if (content && this.dataset.addLabel) content.textContent = this.dataset.addLabel;
    }
    const subscribeBtn = this.refs.subscribeBtn;
    if (subscribeBtn) {
      const subVariantsAttr = subscribeBtn.dataset.subscriptionVariants;
      if (subVariantsAttr !== undefined) {
        // Per-variant map present: show/hide based on whether the current
        // variant has subscription availability.
        let subMap = {};
        try { subMap = JSON.parse(subVariantsAttr || "{}"); } catch {}
        const variantId = String(this.dataset.variantId || "");
        if (subMap[variantId] === true) {
          subscribeBtn.removeAttribute("hidden");
        } else {
          subscribeBtn.setAttribute("hidden", "");
        }
      } else {
        // No map attribute — fall back to the original reveal behaviour
        // (legacy NG-API cards that pre-date the per-variant map).
        subscribeBtn.classList.remove("tw:hidden");
      }
    }
  }

  #updatePrice(variant) {
    const priceEl = this.querySelector("[data-cpc-price-value]");
    if (!priceEl) return;
    const price = this.#formatMoney(variant.price);
    if (variant.compareAt && variant.compareAt > variant.price) {
      priceEl.innerHTML = `${price} <span class="tw:text-[14px] tw:font-normal tw:text-[#6C6C6C] tw:line-through">${this.#formatMoney(
        variant.compareAt
      )}</span>`;
    } else {
      priceEl.textContent = price;
    }
  }

  #updateImage(variant) {
    if (!variant.image) return;
    const track = this.querySelector("[data-cpc-track]");
    if (!track) return;

    // Remembered for #syncHoverImage: the track may still be smooth-scrolling,
    // so reading the visible slide back from scrollLeft is not reliable.
    this._tileImage = variant.image;

    const base = variant.image.split("?")[0];
    const slides = Array.from(track.children);
    const matchIndex = slides.findIndex((slide) => slide.querySelector("img")?.src.split("?")[0] === base);

    if (matchIndex >= 0) {
      track.scrollTo({ left: slides[matchIndex].offsetLeft, behavior: "auto" });
      this.#setActiveDot?.(matchIndex);
      return;
    }

    const firstImg = slides[0]?.querySelector("img");
    if (firstImg) {
      firstImg.src = variant.image;
      firstImg.removeAttribute("srcset");
      track.scrollTo({ left: 0, behavior: "auto" });
    }
  }

  /**
   * Once the shopper has picked an option the tile image is the selection, so the
   * desktop hover overlay (stacked on top of the track, at full opacity while the
   * cursor is over the card during the click) is pointed at the same image —
   * otherwise hover keeps revealing the second image of the original colour.
   * Runs on every option change, not just ones that resolve an image: a size
   * variant has no image of its own, so it falls back to whatever the tile shows.
   * Mirrors ng-product-card.js (MA-2975).
   */
  #syncHoverImage() {
    const hoverImg = this.querySelector("[data-cpc-image-hover]");
    if (!hoverImg) return;

    const tileImg = this.querySelector("[data-cpc-track] img");
    const src = this._tileImage || tileImg?.currentSrc || tileImg?.src;
    if (!src) return;

    hoverImg.src = src;
    hoverImg.removeAttribute("srcset");
  }

  #updateProductLinks(url) {
    if (!url) return;
    this.querySelectorAll('a[href*="/products/"]').forEach((link) => {
      link.setAttribute("href", url);
    });
  }

  /**
   * Notifies the embedded <product-rewards> component (which listens on the
   * section) so BV/IBV/cashback/UC/SRP update for the selected variant.
   * @param {number|string} variantId
   */
  #dispatchVariantUpdate(variantId) {
    this.dispatchEvent(
      new CustomEvent("variant:update", {
        bubbles: true,
        detail: { resource: { id: variantId }, data: { productId: this.dataset.productId } },
      })
    );
  }

  #formatMoney(cents) {
    const amount = (Number(cents) || 0) / 100;

    const template = window.Shopify?.moneyFormat ?? "${{amount}}";
    const rounded = Math.round(amount);
    return template
      .replace(/\{\{\s*amount\s*\}\}/, amount.toFixed(2))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, rounded.toFixed(0))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.toFixed(2).replace(".", ","))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, rounded.toLocaleString("de-DE"));
  }

  /**
   * Wires up the mobile image carousel: clicking a dot scrolls to the matching
   * slide, and scrolling the track updates the active dot. Desktop hover-swap is
   * CSS-driven (group-hover); its source is only touched by #syncHoverImage.
   */
  #initMediaCarousel() {
    const track = this.querySelector("[data-cpc-track]");
    const dots = Array.from(this.querySelectorAll("[data-cpc-dot]"));
    if (!track || dots.length < 2) return;

    this._mediaTrack = track;
    this._mediaDots = dots;

    this._onDotClick = (event) => {
      const index = Number(event.currentTarget.dataset.index || 0);
      const slide = track.children[index];
      if (slide) track.scrollTo({ left: slide.offsetLeft, behavior: "smooth" });
    };
    dots.forEach((dot) => dot.addEventListener("click", this._onDotClick));

    this._onTrackScroll = () => {
      if (this._scrollRaf) return;
      this._scrollRaf = requestAnimationFrame(() => {
        this._scrollRaf = null;
        const width = track.clientWidth || 1;
        this.#setActiveDot(Math.round(track.scrollLeft / width));
      });
    };
    track.addEventListener("scroll", this._onTrackScroll, { passive: true });
  }

  #teardownMediaCarousel() {
    this._mediaDots?.forEach((dot) => dot.removeEventListener("click", this._onDotClick));
    this._mediaTrack?.removeEventListener("scroll", this._onTrackScroll);
    if (this._scrollRaf) cancelAnimationFrame(this._scrollRaf);
  }

  /**
   * Highlights the dot matching the currently visible slide.
   * @param {number} index
   */
  #setActiveDot(index) {
    this._mediaDots?.forEach((dot, i) => {
      dot.setAttribute("aria-current", i === index ? "true" : "false");
    });
  }

  /**
   * Collects hidden `properties[_key]` inputs rendered inside the card (e.g. by
   * product-rewards.liquid with render_cart_properties: true), so cashback / BV / IBV
   * carry into the cart line item the same way they do via a native add-to-cart form.
   * There's no native <form> here — handleAdd POSTs JSON directly — so these inputs
   * are read directly rather than relying on the HTML `form` attribute's association.
   */
  #collectCartProperties() {
    const inputs = this.querySelectorAll('input[name^="properties["]');
    if (!inputs.length) return null;

    const properties = {};
    for (const input of inputs) {
      const match = input.name.match(/^properties\[(.+)\]$/);
      if (match) {
        properties[match[1]] = input.value;
      }
    }
    return Object.keys(properties).length ? properties : null;
  }

  /**
   * Handles the "+ Add" button click.
   * POSTs the first available variant to cart via JSON API.
   */
  async handleAdd() {
    const { addBtn, addBtnContent, addBtnSpinner } = this.refs;
    if (!addBtn) return;

    const variantId = this.dataset.variantId;
    const cartAddUrl = this.dataset.cartAddUrl;

    if (!variantId || !cartAddUrl) return;

    if (addBtn.disabled) return;

    this.#setButtonLoading(addBtn, addBtnContent, addBtnSpinner, true);
    // Block the sibling Subscribe action while the add is in flight so both
    // can't fire at once (no spinner on it — it isn't the one being clicked).
    this.#setSiblingBusy(this.refs.subscribeBtn, true);

    const item = { id: Number(variantId), quantity: 1 };
    const properties = withDistributorIdProperty(this.#collectCartProperties() || {}, {
      isRastellis: this.dataset.isRastellis === "true",
    });
    if (Object.keys(properties).length) item.properties = properties;

    // BCRS fee pairing: the fee follows the SELECTED variant (dataset.variantId
    // tracks the picker), so every variant line gets its own fee child.
    const items = [item];
    const bcrsFeeVariantId = this.dataset.bcrsFeeVariantId;
    if (bcrsFeeVariantId) {
      // Fee first: newest-first cart ordering puts the main line above its fee.
      items.unshift(buildFeeItem(bcrsFeeVariantId, variantId, 1, null));
    }

    try {
      const response = await fetch(cartAddUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          items,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        this.#announceCartAdd(data, variantId);
      } else {
        console.error("Add to cart failed:", data.description || data.message);
      }
    } catch (error) {
      console.error("Add to cart error:", error);
    } finally {
      this.#setButtonLoading(addBtn, addBtnContent, addBtnSpinner, false);
      this.#setSiblingBusy(this.refs.subscribeBtn, false);
    }
  }

  /**
   * Handles the "Subscribe" button click: opens the global subscription drawer for this card's
   * product + currently selected variant (add mode). The drawer owns frequency selection and the
   * actual add-to-cart, so the card just announces intent via SubscriptionOpenEvent.
   */
  handleSubscribe() {
    const handle = this.dataset.productHandle;
    if (!handle) return;

    document.dispatchEvent(
      new SubscriptionOpenEvent({
        handle,
        variantId: this.dataset.variantId,
        mode: "add",
      })
    );
  }

  /**
   * Announces a successful cart add.
   *
   * Dispatches a CartAddEvent so the theme's cart drawer auto-opens on all viewports.
   * The drawer (cart-drawer-component) is rendered once from header-actions at the
   * header root, outside the desktop-only display:none header row.
   *
   * @param {Object} data - The cart add response payload
   * @param {string} variantId - The added variant id
   */
  #announceCartAdd(data, variantId) {
    document.dispatchEvent(
      new CartAddEvent(data, variantId, {
        source: "custom-product-card",
        itemCount: 1,
        productId: this.dataset.productId,
      })
    );
  }

  /**
   * Swaps button content with an existing loading icon while the cart API is pending.
   * @param {HTMLButtonElement} button
   * @param {HTMLElement | undefined} content
   * @param {HTMLElement | undefined} spinner
   * @param {boolean} isLoading
   */
  #setButtonLoading(button, content, spinner, isLoading) {
    button.disabled = isLoading;
    button.setAttribute("aria-busy", isLoading ? "true" : "false");
    content?.classList.toggle("hidden", isLoading);
    spinner?.classList.toggle("hidden", !isLoading);
  }

  /**
   * Disables a sibling action button for the duration of the primary action so
   * two adds can't be triggered at once. Restores the button's PRIOR disabled
   * state (via a data marker) so a button already disabled for another reason
   * — e.g. an unavailable variant — stays disabled when the action completes.
   * @param {HTMLButtonElement | undefined} button
   * @param {boolean} isBusy
   */
  #setSiblingBusy(button, isBusy) {
    if (!button) return;
    if (isBusy) {
      if (button.disabled) return; // already disabled for another reason — leave it
      button.dataset.busyDisabled = "true";
      button.disabled = true;
    } else if (button.dataset.busyDisabled) {
      delete button.dataset.busyDisabled;
      button.disabled = false;
    }
  }
}

if (!customElements.get("custom-product-card")) {
  customElements.define("custom-product-card", CustomProductCard);
}
